import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { getBillUrl, getBillUrlTitle } from "@helpers/bills";
import { GridRow } from "@design-system/Grid/GridRow";
import { GridCol } from "@design-system/Grid/GridCol";
import { EagerLoaded } from "@common/types/base/orm";
import {
    Bill,
    BillSource,
    BillStatus as BillStatusEnum,
} from "@common/types/bill";
import { toUTCDateOnly } from "@common/helpers/date";
import { isBillItemLine } from "@common/helpers/lineItems";
import { formatDateOnly } from "@helpers/date";
import { Amount } from "@design-system/Amount/Amount";
import { Card } from "@design-system/Card/Card";
import { Button } from "@design-system/Button/Button";
import { MemoSection } from "@design-system/MemoSection/MemoSection";
import { AccrualDetailsPanel } from "@components/accounting/AccrualDetailsPanel/AccrualDetailsPanel";
import { AccrualClassField } from "@components/accounting/AccrualDetailsPanel/AccrualClassField";
import { AccrualMatchedTransactionsSection } from "@components/accounting/AccrualDetailsPanel/AccrualMatchedTransactionsSection";
import { useEntitiesById } from "@hooks/useEntities";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { useDeleteBill } from "@api/bill.api";
import { useClasses } from "@api/class.api";
import { useScope } from "@state/scopeContext.lib";
import { useCopyToClipboard } from "@hooks/useCopyToClipboard";
import { AccrualActivity } from "@components/auditLog/accrual/AccrualActivity";
import { AccrualActivityPanel } from "@components/auditLog/accrual/AccrualActivityPanel";
import { ResourceType } from "@common/types/auditLog";
import { ArrowCornerIcon, RemoveIcon } from "@icons";
import { VStack } from "@design-system/VStack/VStack";
import { BillStatus } from "./BillStatus";
import { BillStatusSelect } from "./BillStatusSelect";
import { BillDocuments } from "./BillDocuments";
import { BillDocumentsCompact } from "./BillDocumentsCompact";
import { BillDetailsSchedule } from "./schedules/BillDetailsSchedule";
import { BillScheduleCallbacks } from "./schedules/useBillScheduleCallbacks";
import { createBillsUrl } from "./lib/billsUrl";
import styles from "./BillDetails.module.scss";

interface BillLineItemProps {
    label: string | null;
    amount: number;
    showNegative?: boolean;
}

const BillLineItem: React.FC<BillLineItemProps> = ({
    label,
    amount,
    showNegative,
}) => (
    <GridRow gap="0">
        <GridCol sm={6} className={styles.dataLabel}>
            {label}
        </GridCol>
        <GridCol sm={6} className={styles.dataValue}>
            <Amount
                amount={amount}
                separateCurrencySign
                showNegative={showNegative}
            />
        </GridCol>
    </GridRow>
);

interface BillDetailsProps {
    bill: EagerLoaded<Bill, "lines">;
    setBill: (bill?: Bill) => void;
    onEdit?: () => void;
    onUpdateMemo?: (memo: string | null) => Promise<void>;
    onUpdateStatus?: (params: {
        status: BillStatusEnum;
        paidAt?: string | null;
    }) => Promise<void>;
    hideCloseButton?: boolean;
    className?: string;
    scheduleCallbacks?: BillScheduleCallbacks;
}

export const BillDetails: React.FC<BillDetailsProps> = ({
    bill,
    setBill,
    onEdit,
    onUpdateMemo,
    onUpdateStatus,
    hideCloseButton = false,
    className,
    scheduleCallbacks,
}) => {
    const { manualAccrualCreation } = useTypedFlags();
    const { isActivated: areClassesActivated } = useClasses();
    const isAccrualLedgerEnabled = useIsAccrualLedgerEnabled();
    const { workspaceId } = useScope();
    const entitiesById = useEntitiesById();
    const entity = entitiesById.get(bill.entityId);
    const deleteBillMutation = useDeleteBill();

    const selectedClassIds = useMemo(() => {
        const classIds = new Set<string>();
        (bill.lines ?? []).forEach((line) => {
            line.classAssignments?.forEach((assignment) => {
                classIds.add(assignment.classId);
            });
        });
        return Array.from(classIds);
    }, [bill.lines]);

    const classNode = areClassesActivated ? (
        <AccrualClassField classIds={selectedClassIds} />
    ) : undefined;

    const handleDelete = useCallback(async () => {
        if (!workspaceId) {
            return;
        }
        await deleteBillMutation.mutateAsync({
            workspaceId,
            billId: bill.id,
        });
        setBill(undefined);
    }, [workspaceId, bill.id, deleteBillMutation, setBill]);

    const copyToClipboard = useCopyToClipboard({
        successMessage: "Link copied to clipboard",
    });

    const handleCopyUrl = useCallback(() => {
        const url = `${window.location.origin}${createBillsUrl({ billIds: [bill.id] })}`;
        copyToClipboard(url);
    }, [bill.id, copyToClipboard]);

    const externalStatusBadge = bill.externalStatus ? (
        <BillStatus status={bill.externalStatus} showText />
    ) : undefined;
    const statusBadge = <BillStatus status={bill.status} showText />;

    if (manualAccrualCreation) {
        const isDeletable = bill.source === BillSource.IMPORTER;
        const isEditable = bill.source === BillSource.IMPORTER;
        const billUrl = getBillUrl(bill);

        return (
            <AccrualDetailsPanel
                statusBadge={externalStatusBadge ?? statusBadge}
                metadata={{
                    externalStatusText: externalStatusBadge,
                    statusText: isAccrualLedgerEnabled
                        ? statusBadge
                        : undefined,
                    statusSelect:
                        isAccrualLedgerEnabled && onUpdateStatus ? (
                            <BillStatusSelect
                                value={bill.status}
                                onValueChange={onUpdateStatus}
                            />
                        ) : undefined,
                    counterpartyName: bill.counterparty?.name,
                    counterpartyEmail: bill.counterparty?.email,
                    createdAt: bill.dateOnly ?? bill.createdAt,
                    dueDate: bill.dueDateOnly ?? bill.due,
                    entity,
                    classNode,
                    recognitionScheduleNode: scheduleCallbacks ? (
                        <BillDetailsSchedule
                            bill={bill}
                            callbacks={scheduleCallbacks}
                        />
                    ) : undefined,
                    recognitionScheduleLabel: "Expense schedule",
                    documentSection: (
                        <BillDocumentsCompact
                            billId={bill.id}
                            financialDocumentIds={bill.financialDocumentIds}
                        />
                    ),
                    memo: bill.memo,
                    memoLabel: "Memo",
                    onUpdateMemo,
                    onCopyUrl: handleCopyUrl,
                    urlLabel: "URL",
                }}
                amount={bill.total}
                matchedTransactionsSection={
                    <AccrualMatchedTransactionsSection
                        documentType="bill"
                        documentId={bill.id}
                        source={bill.source}
                    />
                }
                activitySection={
                    <AccrualActivityPanel
                        resourceType={ResourceType.BILL}
                        resourceId={bill.id}
                        entityId={bill.entityId}
                    />
                }
                onEdit={isEditable ? onEdit : undefined}
                onView={
                    billUrl
                        ? () =>
                              window.open(
                                  billUrl,
                                  "_blank",
                                  "noopener,noreferrer",
                              )
                        : undefined
                }
                viewTooltip="View bill"
                onDelete={isDeletable ? handleDelete : undefined}
                deleteEntityName="bill"
                onClose={() => setBill(undefined)}
                hideCloseButton={hideCloseButton}
                className={className}
            />
        );
    }

    const showSubtotal = bill.subtotal !== bill.total;
    const showTax = bill.tax > 0;
    const showDiscount = bill.discount > 0;
    const deposit = bill.deposit ?? 0;
    const showDeposit = deposit > 0;
    const showExtras = showTax || showDiscount || showDeposit || showSubtotal;

    return (
        <Card className={classNames(styles.card, "p-0", className)}>
            <section className={styles.section}>
                <div className={styles.header}>
                    {bill.externalStatus ? (
                        <BillStatus status={bill.externalStatus} showText />
                    ) : (
                        <BillStatus status={bill.status} showText />
                    )}
                    {!hideCloseButton && (
                        <Button
                            variant="secondary"
                            size="md"
                            onClick={() => setBill(undefined)}
                            data-testid="bill-details-close"
                            iconOnly
                            aria-label="Close bill details"
                        >
                            <RemoveIcon />
                        </Button>
                    )}
                </div>
                <VStack>
                    {bill.counterparty && (
                        <div className={styles.counterparty}>
                            <span className={styles.counterpartyName}>
                                {bill.counterparty.name}
                            </span>
                            <span className={styles.counterpartyEmail}>
                                {bill.counterparty.email}
                            </span>
                        </div>
                    )}

                    <Amount
                        className={styles.amount}
                        amount={bill.total}
                        separateCurrencySign
                        showNegative={false}
                    />

                    {bill.paidAt && (
                        <div className={styles.paidOn}>
                            Paid on{" "}
                            {formatDateOnly(
                                bill.paidDateOnly ??
                                    (bill.paidAt
                                        ? toUTCDateOnly(bill.paidAt)
                                        : null),
                            )}
                        </div>
                    )}
                </VStack>
                <Button
                    variant="primary"
                    size="md"
                    onClick={() => {
                        window.open(
                            getBillUrl(bill),
                            "_blank",
                            "noopener,noreferrer",
                        );
                    }}
                    className={styles.viewButton}
                    rightIcon={<ArrowCornerIcon className={styles.arrowIcon} />}
                >
                    {getBillUrlTitle(bill)}
                </Button>
            </section>

            <section className={classNames(styles.section, styles.dataSection)}>
                {externalStatusBadge && (
                    <GridRow gap="0">
                        <GridCol sm={6} className={styles.dataLabel}>
                            External status
                        </GridCol>
                        <GridCol sm={6} className={styles.dataValue}>
                            {externalStatusBadge}
                        </GridCol>
                    </GridRow>
                )}
                {isAccrualLedgerEnabled && (
                    <GridRow gap="0">
                        <GridCol sm={6} className={styles.dataLabel}>
                            Status
                        </GridCol>
                        <GridCol sm={6} className={styles.dataValue}>
                            {statusBadge}
                        </GridCol>
                    </GridRow>
                )}
                <GridRow gap="0">
                    <GridCol sm={6} className={styles.dataLabel}>
                        Due Date
                    </GridCol>
                    <GridCol sm={6} className={styles.dataValue}>
                        {formatDateOnly(bill.dueDateOnly)}
                    </GridCol>
                </GridRow>
                <GridRow gap="0">
                    <GridCol sm={6} className={styles.dataLabel}>
                        Date
                    </GridCol>
                    <GridCol sm={6} className={styles.dataValue}>
                        {formatDateOnly(bill.dateOnly)}
                    </GridCol>
                </GridRow>
            </section>

            <section
                className={classNames(styles.section, styles.lineItemsSection)}
            >
                <span className={styles.lineItemsTitle}>Bill Details</span>
                {bill.lines?.filter(isBillItemLine).map((line) => (
                    <BillLineItem
                        key={line.id}
                        label={line.description}
                        amount={line.amount}
                        showNegative={showSubtotal}
                    />
                ))}
                {showExtras && (
                    <div className={styles.lineItemsSeparator}></div>
                )}
                {showSubtotal && (
                    <BillLineItem
                        label="Subtotal"
                        amount={bill.subtotal}
                        showNegative={false}
                    />
                )}
                {bill.discount > 0 && (
                    <BillLineItem
                        label="Discount"
                        amount={-bill.discount}
                        showNegative
                    />
                )}
                {bill.tax > 0 && (
                    <BillLineItem
                        label="Tax"
                        amount={bill.tax}
                        showNegative={false}
                    />
                )}
                {showDeposit && (
                    <BillLineItem
                        label="Deposit"
                        amount={-deposit}
                        showNegative
                    />
                )}
                <div className={styles.lineItemsSeparator}></div>
                <GridRow gap="0" className={styles.lineItemsTotal}>
                    <GridCol sm={6} className={styles.dataLabel}>
                        Total
                    </GridCol>
                    <GridCol sm={6} className={styles.dataValue}>
                        <Amount
                            amount={bill.total}
                            separateCurrencySign
                            showNegative={false}
                        />
                    </GridCol>
                </GridRow>
            </section>

            {onUpdateMemo && (
                <MemoSection
                    memo={bill.memo}
                    onUpdateMemo={onUpdateMemo}
                    className={classNames(styles.section, styles.dataSection)}
                />
            )}

            <section className={classNames(styles.section, styles.dataSection)}>
                <BillDocuments
                    billId={bill.id}
                    financialDocumentIds={bill.financialDocumentIds}
                />
            </section>

            <section className={classNames(styles.section, styles.dataSection)}>
                <AccrualActivity
                    resourceType={ResourceType.BILL}
                    resourceId={bill.id}
                    entityId={bill.entityId}
                />
            </section>
        </Card>
    );
};
